// apps/app/src/lib/query-client.ts

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-client';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A clinic dashboard is left open all day on a reception monitor. Refetch
      // on focus keeps it current without polling.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry what will fail identically. A 403 is not a network blip.
        if (error instanceof ApiError && error.status && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Cache keys in one place, so an invalidation cannot miss by a typo. */
export const keys = {
  tenant: ['tenant'] as const,
  profile: ['profile'] as const,
  onboarding: ['onboarding'] as const,
  treatments: ['treatments'] as const,
  treatment: (id: string) => ['treatments', id] as const,
  /** SkinVizo's ready-made treatments, as this clinic sees them. */
  treatmentLibrary: ['treatments', 'library'] as const,
  platformTreatmentLibrary: ['platform', 'treatment-library'] as const,
  products: ['products'] as const,
  patients: (filters?: unknown) => ['patients', filters] as const,
  patient: (id: string) => ['patients', id] as const,
  timeline: (id: string) => ['patients', id, 'timeline'] as const,
  scans: (filters?: unknown) => ['scans', filters] as const,
  scan: (id: string) => ['scans', id] as const,
  summary: ['scans', 'summary'] as const,
  leads: ['leads'] as const,
  analytics: (kind: string, days: number) => ['analytics', kind, days] as const,
  billing: ['billing'] as const,
  widgetConfig: ['widget', 'config'] as const,
  domains: ['domains'] as const,
  team: ['users'] as const,
  apiKeys: ['api-keys'] as const,
  platformOverview: ['platform', 'overview'] as const,
  platformSettings: ['platform', 'settings'] as const,
  platformStorageStatus: ['platform', 'storage', 'status'] as const,
  platformMediaLibrary: ['platform', 'storage', 'library'] as const,
  media: ['media'] as const,
  /** Stripe's mode (test or live), which keys each mode has, and what a switch would let go of. */
  platformStripe: ['platform', 'stripe'] as const,
  verificationQueue: ['platform', 'verification'] as const,
  platformClinics: (filters?: unknown) => ['platform', 'clinics', filters] as const,
  platformClinic: (id: string) => ['platform', 'clinics', id] as const,
  platformStaff: ['platform', 'staff'] as const,
  platformPlans: ['platform', 'plans'] as const,
  platformCoupons: ['platform', 'coupons'] as const,
  platformCouponRedemptions: (id: string) => ['platform', 'coupons', id, 'redemptions'] as const,
  platformClinicCoupon: (id: string) => ['platform', 'clinics', id, 'coupon'] as const,
  /** The real AI cost of a scan, platform-wide and per plan, with exchange rates. */
  platformAiCosts: ['platform', 'billing', 'ai-costs'] as const,
  /** This month's revenue, AI cost and profit across every clinic. */
  platformProfit: ['platform', 'billing', 'profit'] as const,
  // Under the clinic's own key, so invalidating the clinic refreshes its billing too.
  platformClinicBilling: (id: string) => ['platform', 'clinics', id, 'billing'] as const,
  /** Which email provider would send right now, and from what address. */
  platformEmailStatus: ['platform', 'emails', 'status'] as const,
  /** The email log, by filter; the last seven days' counts ride along. */
  platformEmails: (filters?: unknown) => ['platform', 'emails', 'log', filters] as const,
  platformEmailTemplates: ['platform', 'emails', 'templates'] as const,
  platformEmailTemplate: (key: string) => ['platform', 'emails', 'templates', key] as const,
};
